import { isMainnet } from "@/constants/flags.constant";
import type { TokenGatewayResponse, TokenGatewayQueryParams } from "@/features/bridge/types/token-gateway.types";

/**
 * Fetches token gateway addresses from the Blessnet API
 * These are self-bridging tokens that have gateway features built-in
 *
 * @param originChainId - The chain ID where the token originates (from/origin chain)
 * @param params - Query parameters for filtering the results
 * @returns Promise that resolves to the token gateway response
 * @throws Will throw an error if the fetch operation fails
 */
export const getTokenGatewayAddresses = async (
  originChainId: number,
  params: TokenGatewayQueryParams = {}
): Promise<TokenGatewayResponse> => {
  // Default parameters for token gateway query
  const defaultParams: TokenGatewayQueryParams = {
    type: 'tokenGateway',
    chainId: originChainId, // Use the origin chain ID
    isActive: true,
    ...params,
  };

  // Build query string
  const queryString = new URLSearchParams(
    Object.entries(defaultParams).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = String(value);
      }
      return acc;
    }, {} as Record<string, string>)
  ).toString();

  // Use the internal API proxy to make the request
  const network = isMainnet ? "mainnet" : "sepolia";
  const apiRoute = `/api/blessnet/watched-addresses?network=${network}&${queryString}`;

  console.log('🔗 [TOKEN GATEWAY] Making API request:', {
    network,
    queryString,
    fullUrl: apiRoute,
    params: defaultParams
  });

  try {
    const response = await fetch(apiRoute);

    if (!response.ok) {
      throw new Error(`Error fetching token gateway addresses: ${response.status} ${response.statusText}`);
    }

    const data: TokenGatewayResponse = await response.json();

    console.log('🔗 [TOKEN GATEWAY] API Response:', {
      status: response.status,
      count: data.count,
      addresses: data.watchedAddresses.map(addr => addr.address.toLowerCase()),
      chainId: defaultParams.chainId,
      rawResponse: data
    });

    return data;
  } catch (error) {
    console.error('🚨 [TOKEN GATEWAY] Error fetching addresses:', error);
    throw error;
  }
};
